import { useCallback, useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";
import { normalizeTaxRateValue } from "@/lib/articleConstants";
import { TaxRateSelect } from "@/pages/stammdaten/TaxRateSelect";
import type { ChartAccountTemplate, ChartOfAccountsId, ChartTemplate } from "@/types/camping";

const EMPTY_FORM = {
  account_number: "",
  name: "",
  tax_rate_percent: "19",
};

export interface CreateAccountModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateAccountModal({ open, onClose, onCreated }: CreateAccountModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [charts, setCharts] = useState<ChartTemplate[]>([]);
  const [selectedChart, setSelectedChart] = useState<ChartOfAccountsId | "">("");
  const [templateAccounts, setTemplateAccounts] = useState<ChartAccountTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTemplateAccounts = useCallback((chartId: ChartOfAccountsId) => {
    api
      .get<ChartAccountTemplate[]>(`/api/v1/accounts/chart-templates/${chartId}`)
      .then(setTemplateAccounts)
      .catch(() => setTemplateAccounts([]));
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setSelectedChart("");
    setSelectedTemplate("");
    setTemplateAccounts([]);
    setError(null);
    setSubmitting(false);
    api
      .get<ChartTemplate[]>("/api/v1/accounts/chart-templates")
      .then(setCharts)
      .catch(() => setCharts([]));
  }, [open]);

  useEffect(() => {
    if (!selectedChart) {
      setTemplateAccounts([]);
      setSelectedTemplate("");
      return;
    }
    loadTemplateAccounts(selectedChart);
    setSelectedTemplate("");
  }, [selectedChart, loadTemplateAccounts]);

  function onTemplateSelect(value: string) {
    setSelectedTemplate(value);
    if (!value) return;
    const template = templateAccounts.find((t) => t.account_number === value);
    if (template) {
      setForm({
        account_number: template.account_number,
        name: template.name,
        tax_rate_percent: normalizeTaxRateValue(template.tax_rate_percent) || "19",
      });
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/accounts", {
        account_number: form.account_number.trim(),
        name: form.name.trim(),
        tax_rate_percent: Number(form.tax_rate_percent),
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neues Erlöskonto"
      description="Aus Kontenrahmen übernehmen oder manuell eingeben – Bezeichnung und Nummer sind danach änderbar"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        {charts.length > 0 ? (
          <div className="space-y-3 rounded-lg border border-separator bg-surface-secondary p-3">
            <p className="text-sm font-medium">Aus Kontenrahmen (optional)</p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <label className="flex min-w-[8rem] flex-1 flex-col gap-1 text-sm">
                Rahmen
                <select
                  className="eh-select-input"
                  value={selectedChart}
                  onChange={(e) =>
                    setSelectedChart((e.target.value || "") as ChartOfAccountsId | "")
                  }
                >
                  <option value="">— manuell —</option>
                  {charts.map((chart) => (
                    <option key={chart.id} value={chart.id}>
                      {chart.label}
                    </option>
                  ))}
                </select>
              </label>
              {selectedChart && templateAccounts.length > 0 ? (
                <label className="flex flex-1 flex-col gap-1 text-sm">
                  Vorschlag
                  <select
                    className="eh-select-input"
                    value={selectedTemplate}
                    onChange={(e) => onTemplateSelect(e.target.value)}
                  >
                    <option value="">— wählen —</option>
                    {templateAccounts.map((t) => (
                      <option key={t.account_number} value={t.account_number}>
                        {t.account_number} – {t.name} ({normalizeTaxRateValue(t.tax_rate_percent)} %)
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          </div>
        ) : null}
        <label className="flex flex-col gap-1 text-sm">
          Kontonummer
          <input
            required
            autoFocus
            inputMode="numeric"
            pattern="[0-9]+"
            title="Nur Ziffern"
            className="eh-input w-40"
            value={form.account_number}
            onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Bezeichnung
          <input
            required
            className="eh-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-label">Mehrwertsteuer</legend>
          <p className="text-xs text-label-secondary">
            Artikel mit diesem Konto übernehmen den Satz. Ein eigener Satz am Artikel ist nicht
            möglich.
          </p>
          <TaxRateSelect
            value={form.tax_rate_percent}
            onChange={(tax_rate_percent) => setForm((f) => ({ ...f, tax_rate_percent }))}
          />
        </fieldset>
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting}>
            Anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
